import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./ShipperGuidePage.module.scss";
import { LanguageDropdown } from "../../../components/LanguageSelector/LanguageSelector";
import LogoIcon from "../../../components/shared/icons/LogoIcon";
import { useWindowSize } from "../../../hooks/useWindowSize";
import classNames from "classnames";
import { AlarmClockPlusIcon, Share2Icon, ShareIcon, Trash2Icon, Undo2Icon } from "lucide-react";

const ShipperGuidePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isDesktop } = useWindowSize();

  const [activeSection, setActiveSection] = useState("about");
  const [activeMode, setActiveMode] = useState("auto");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const showSection = (sectionId, scrollTarget) => {
    setActiveSection(sectionId);
    window.scrollTo(0, 0);
    setSidebarOpen(false);
    if (scrollTarget) {
      setTimeout(() => {
        const el = document.getElementById(`${sectionId}-${scrollTarget}`);
        if (el) {
          const headerOffset = 80; // Fixed header height (56px) + padding
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  const switchMode = (mode) => {
    setActiveMode(mode);
    showSection(mode === "auto" ? "auto-create" : "avia-create");
  };

  const sectionClass = (id) => `${styles.pageSection} ${activeSection === id ? styles.visible : ""}`;

  const navLinkClass = (sectionId, isSub = false) => {
    let cls = styles.navLink;
    if (isSub) cls += ` ${styles.navSub}`;
    if (activeSection === sectionId && !isSub) cls += ` ${styles.active}`;
    return cls;
  };

  return (
    <div className={styles.guideWrapper}>
      {/* HEADER */}
      <header className={styles.header}>
        <div className={styles.headerLogo} onClick={() => navigate("/main")} style={{ cursor: "pointer" }}>
          <LogoIcon />
        </div>
        <div className={styles.headerSep}></div>
        {!isMobile && <div className={styles.headerTitle}>{t("shipperGuidePage.headerTitle")}</div>}
        <div className={"ms-auto"}>
          <LanguageDropdown />
        </div>
        <button className={styles.mobileToggle} onClick={() => setSidebarOpen(!sidebarOpen)}>
          ☰
        </button>
      </header>

      {/* SIDEBAR */}
      <nav className={`${styles.sidebar} ${sidebarOpen ? styles.open : ""}`}>
        <div className={styles.navGroup}>
          <div className={styles.navGroupLabel}>{t("shipperGuidePage.nav.general")}</div>
          <a className={navLinkClass("about")} onClick={() => showSection("about")}>
            <span className={styles.navIcon}>1</span> {t("shipperGuidePage.nav.about")}
          </a>
          <a className={navLinkClass("differ")} onClick={() => showSection("differ")}>
            <span className={styles.navIcon}>2</span> {t("shipperGuidePage.nav.differ")}
          </a>
          <a className={navLinkClass("verification")} onClick={() => showSection("verification")}>
            <span className={styles.navIcon}>3</span> {t("shipperGuidePage.nav.verification")}
          </a>
        </div>

        <div className={styles.navDivider}></div>

        <div className={styles.navGroup}>
          <div className={styles.navGroupLabel}>{t("shipperGuidePage.nav.sections")}</div>
          <div className={styles.navTabSwitch}>
            <button
              className={`${styles.navTabBtn} ${activeMode === "auto" ? styles.active : ""}`}
              onClick={() => switchMode("auto")}
            >
              {t("shipperGuidePage.nav.auto")}
            </button>
            <button
              className={`${styles.navTabBtn} ${activeMode === "avia" ? styles.active : ""}`}
              onClick={() => switchMode("avia")}
            >
              {t("shipperGuidePage.nav.avia")}
            </button>
          </div>
        </div>

        {/* AUTO NAV */}
        {activeMode === "auto" && (
          <div className={styles.navGroup}>
            <a className={navLinkClass("auto-create")} onClick={() => showSection("auto-create")}>
              <span className={styles.navIcon}>1</span> {t("shipperGuidePage.nav.createOrder")}
            </a>
            <a
              className={navLinkClass("auto-create", true)}
              onClick={() => showSection("auto-create", "tender")}
            >
              {t("shipperGuidePage.nav.tender")}
            </a>
            <a
              className={navLinkClass("auto-create", true)}
              onClick={() => showSection("auto-create", "direct")}
            >
              {t("shipperGuidePage.nav.directAssignment")}
            </a>
            <a
              className={navLinkClass("auto-create", true)}
              onClick={() => showSection("auto-create", "public")}
            >
              {t("shipperGuidePage.nav.publicMarket")}
            </a>
            <a className={navLinkClass("auto-partners")} onClick={() => showSection("auto-partners")}>
              <span className={styles.navIcon}>2</span> {t("shipperGuidePage.nav.partners")}
            </a>
            <a className={navLinkClass("auto-order")} onClick={() => showSection("auto-order")}>
              <span className={styles.navIcon}>3</span> {t("shipperGuidePage.nav.orderSection")}
            </a>
            <a
              className={navLinkClass("auto-order", true)}
              onClick={() => showSection("auto-order", "details")}
            >
              {t("shipperGuidePage.nav.orderDetails")}
            </a>
            <a
              className={navLinkClass("auto-order", true)}
              onClick={() => showSection("auto-order", "filter")}
            >
              {t("shipperGuidePage.nav.filterExport")}
            </a>
            <a
              className={navLinkClass("auto-order", true)}
              onClick={() => showSection("auto-order", "notif")}
            >
              {t("shipperGuidePage.nav.orderStatuses")}
            </a>
            <a
              className={navLinkClass("auto-order", true)}
              onClick={() => showSection("auto-order", "history")}
            >
              {t("shipperGuidePage.nav.orderHistory")}
            </a>
            <a className={navLinkClass("auto-marketplace")} onClick={() => showSection("auto-marketplace")}>
              <span className={styles.navIcon}>4</span> {t("shipperGuidePage.nav.marketplace")}
            </a>
            <a className={navLinkClass("auto-analytics")} onClick={() => showSection("auto-analytics")}>
              <span className={styles.navIcon}>5</span> {t("shipperGuidePage.nav.analytics")}
            </a>
            <a className={navLinkClass("auto-staff")} onClick={() => showSection("auto-staff")}>
              <span className={styles.navIcon}>6</span> {t("shipperGuidePage.nav.staff")}
            </a>
          </div>
        )}

        {/* AVIA NAV */}
        {activeMode === "avia" && (
          <div className={styles.navGroup}>
            <a className={navLinkClass("avia-create")} onClick={() => showSection("avia-create")}>
              <span className={styles.navIcon}>1</span> {t("shipperGuidePage.nav.createOrder")}
            </a>
            <a
              className={navLinkClass("avia-create", true)}
              onClick={() => showSection("avia-create", "tender")}
            >
              {t("shipperGuidePage.nav.tender")}
            </a>
            <a
              className={navLinkClass("avia-create", true)}
              onClick={() => showSection("avia-create", "direct")}
            >
              {t("shipperGuidePage.nav.directAssignment")}
            </a>
            <a
              className={navLinkClass("avia-create", true)}
              onClick={() => showSection("avia-create", "public")}
            >
              {t("shipperGuidePage.nav.publicMarket")}
            </a>
            <a className={navLinkClass("avia-partners")} onClick={() => showSection("avia-partners")}>
              <span className={styles.navIcon}>2</span> {t("shipperGuidePage.nav.partners")}
            </a>
            <a className={navLinkClass("avia-order")} onClick={() => showSection("avia-order")}>
              <span className={styles.navIcon}>3</span> {t("shipperGuidePage.nav.orderSection")}
            </a>
            <a
              className={navLinkClass("avia-order", true)}
              onClick={() => showSection("avia-order", "details")}
            >
              {t("shipperGuidePage.nav.orderDetails")}
            </a>
            <a
              className={navLinkClass("avia-order", true)}
              onClick={() => showSection("avia-order", "filter")}
            >
              {t("shipperGuidePage.nav.filterExport")}
            </a>
            <a
              className={navLinkClass("avia-order", true)}
              onClick={() => showSection("avia-order", "notif")}
            >
              {t("shipperGuidePage.nav.orderStatuses")}
            </a>
            <a
              className={navLinkClass("avia-order", true)}
              onClick={() => showSection("avia-order", "history")}
            >
              {t("shipperGuidePage.nav.orderHistory")}
            </a>
            <a className={navLinkClass("avia-marketplace")} onClick={() => showSection("avia-marketplace")}>
              <span className={styles.navIcon}>4</span> {t("shipperGuidePage.nav.marketplace")}
            </a>
            <a className={navLinkClass("avia-analytics")} onClick={() => showSection("avia-analytics")}>
              <span className={styles.navIcon}>5</span> {t("shipperGuidePage.nav.analytics")}
            </a>
            <a className={navLinkClass("avia-staff")} onClick={() => showSection("avia-staff")}>
              <span className={styles.navIcon}>6</span> {t("shipperGuidePage.nav.staff")}
            </a>
          </div>
        )}
      </nav>

      {/* MAIN CONTENT */}
      <main className={styles.main}>
        {/* ==================== ABOUT ==================== */}
        <div className={sectionClass("about")} id="about">
          {isMobile && <div className={cn(styles.headerTitle)}>{t("shipperGuidePage.headerTitle")}</div>}
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.about.title")}</h1>
          <p className={styles.bodyText}>{t("shipperGuidePage.about.description1")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.about.description2")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.about.helpsTitle")}</h3>
          {["help1", "help2", "help3", "help4"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.about.${key}`)}</h4>
              </div>
            </div>
          ))}

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.about.callout")}</div>
          </div>
        </div>

        {/* ==================== DIFFER ==================== */}
        <div className={sectionClass("differ")} id="differ">
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.differ.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.differ.subtitle")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.differ.description1")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.differ.description2")}</p>

          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div className={styles.featureRow} key={i}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.differ.feature${i}Title`)}</h4>
                <p>{t(`shipperGuidePage.differ.feature${i}Desc`)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ==================== VERIFICATION ==================== */}
        <div className={sectionClass("verification")} id="verification">
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.verification.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.verification.subtitle")}</p>
          <p className={styles.bodyText}>
            <strong>{t("shipperGuidePage.verification.whyNeeded")}</strong>{" "}
            {t("shipperGuidePage.verification.whyNeededDesc")}
          </p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`shipperGuidePage.verification.step${i}Title`)}</h4>
                  <p>{t(`shipperGuidePage.verification.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.verification.callout")}</div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* AUTO SECTIONS */}
        {/* ============================================================ */}

        {/* AUTO: CREATE ORDER */}
        <div className={sectionClass("auto-create")} id="auto-create">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoCreate.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoCreate.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoCreate.step1Title")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.blockFrom")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.blockTo")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.blockDetails")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoCreate.step2Title")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.step2Desc")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoCreate.step3Title")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.step3Desc")}</p>

          {/* TENDER */}
          <h3 className={styles.sectionHeading} id="auto-create-tender">
            {t("shipperGuidePage.autoCreate.tenderTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.tenderDesc")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.tenderWhen")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoCreate.tenderStep1")}</h4>
                <p>{t("shipperGuidePage.autoCreate.tenderStep1Desc")}</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>2</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoCreate.tenderStep2")}</h4>
                <p>{t("shipperGuidePage.autoCreate.tenderStep2Desc")}</p>
              </div>
            </div>
          </div>

          {/* DIRECT */}
          <h3 className={styles.sectionHeading} id="auto-create-direct">
            {t("shipperGuidePage.autoCreate.directTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.directDesc")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.directWhen")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoCreate.directStep1")}</h4>
                <p>{t("shipperGuidePage.autoCreate.directStep1Desc")}</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>2</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoCreate.directStep2")}</h4>
                <p>{t("shipperGuidePage.autoCreate.directStep2Desc")}</p>
              </div>
            </div>
          </div>

          {/* PUBLIC */}
          <h3 className={styles.sectionHeading} id="auto-create-public">
            {t("shipperGuidePage.autoCreate.publicTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.publicDesc")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoCreate.publicWhen")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoCreate.publicStep1")}</h4>
                <p>{t("shipperGuidePage.autoCreate.publicStep1Desc")}</p>
              </div>
            </div>
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.autoCreate.callout")}</div>
          </div>
        </div>

        {/* AUTO: PARTNERS */}
        <div className={sectionClass("auto-partners")} id="auto-partners">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoPartners.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoPartners.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoPartners.howToAdd")}</h3>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoPartners.step1")}</h4>
                <p>{t("shipperGuidePage.autoPartners.step1Desc")}</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>2</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.autoPartners.step2")}</h4>
                <p>{t("shipperGuidePage.autoPartners.step2Desc")}</p>
              </div>
            </div>
          </div>

          <div className={styles.cardsGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#DBEAFE" }}>
                A
              </div>
              <h4>{t("shipperGuidePage.autoPartners.byEmail")}</h4>
              <p>{t("shipperGuidePage.autoPartners.byEmailDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#D1FAE5" }}>
                B
              </div>
              <h4>{t("shipperGuidePage.autoPartners.byLink")}</h4>
              <p>{t("shipperGuidePage.autoPartners.byLinkDesc")}</p>
            </div>
          </div>
        </div>

        {/* AUTO: ORDER SECTION */}
        <div className={sectionClass("auto-order")} id="auto-order">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoOrder.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoOrder.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.autoOrder.marketplaceCallout")}</div>
          </div>

          {/* DETAILS */}
          <h3 className={styles.sectionHeading} id="auto-order-details">
            {t("shipperGuidePage.autoOrder.detailsTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.detailsDesc")}</p>
          <p className={styles.bodyText}>
            <strong>{t("shipperGuidePage.autoOrder.toolsTitle")}</strong>
          </p>

          {[
            { key: "viewsAnalytics", descKey: "viewsAnalyticsDesc" },
            { key: "editing", descKey: "editingDesc" },
            { key: "copying", descKey: "copyingDesc" },
            { key: "chatPartner", descKey: "chatPartnerDesc" },
            { key: "historyTitle", descKey: "historyFeatureDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.autoOrder.${key}`)}</h4>
                <p>{t(`shipperGuidePage.autoOrder.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <p className={styles.bodyText} style={{ marginTop: 24 }}>
            <strong>{t("shipperGuidePage.autoOrder.additionalMenu")}</strong>
          </p>
          <div className={styles.contextMenu}>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <ShareIcon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.autoOrder.menuSendMarketplace")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <AlarmClockPlusIcon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.autoOrder.menuExtendTender")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <Share2Icon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.autoOrder.menuShareOrder")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <Undo2Icon size={18} color="#E53935" />
              </span>{" "}
              {t("shipperGuidePage.autoOrder.menuRecallOrder")}
            </div>
            <div className={`${styles.contextMenuItem} ${styles.danger}`}>
              <span className={styles.cmIcon}>
                <Trash2Icon size={18} color="#E53935" />
              </span>{" "}
              {t("shipperGuidePage.autoOrder.menuDeleteOrder")}
            </div>
          </div>

          <div style={{ margin: "20px 0" }}>
            <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.menuSendMarketplaceDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.menuExtendTenderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.menuShareOrderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.menuRecallOrderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.menuDeleteOrderDesc")}</p>
          </div>

          <p className={styles.subHeading}>{t("shipperGuidePage.autoOrder.acceptOfferTitle")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.acceptOfferDesc")}</p>

          {/* FILTER & EXPORT */}
          <h3 className={styles.sectionHeading} id="auto-order-filter">
            {t("shipperGuidePage.autoOrder.filterTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.filterDesc")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.exportDesc")}</p>

          {/* STATUSES */}
          <h3 className={styles.sectionHeading} id="auto-order-notif">
            {t("shipperGuidePage.autoOrder.statusesTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.statusesDesc")}</p>

          <div className={styles.statusFlow}>
            {[
              {
                dotClass: styles.statusDotOrange,
                key: "status1",
                descKey: "status1Desc",
                hasConnector: true,
              },
              { dotClass: styles.statusDotGlobe, key: "status2", descKey: "status2Desc", hasConnector: true },
              { dotClass: styles.statusDotGray, key: "status3", descKey: "status3Desc", hasConnector: true },
              { dotClass: styles.statusDotPink, key: "status4", descKey: "status4Desc", hasConnector: true },
              { dotClass: styles.statusDotBlue, key: "status5", descKey: "status5Desc", hasConnector: true },
              {
                dotClass: styles.statusDotPurple,
                key: "status6",
                descKey: "status6Desc",
                hasConnector: true,
              },
              {
                dotClass: styles.statusDotPurple,
                key: "status7",
                descKey: "status7Desc",
                hasConnector: true,
              },
              { dotClass: styles.statusDotGreen, key: "status8", descKey: "status8Desc", hasConnector: true },
              { dotClass: styles.statusDotBlue, key: "status9", descKey: "status9Desc", hasConnector: true },
              {
                dotClass: styles.statusDotBlue,
                key: "status10",
                descKey: "status10Desc",
                hasConnector: true,
              },
              {
                dotClass: styles.statusDotOrange,
                key: "status11",
                descKey: "status11Desc",
                hasConnector: true,
              },
              {
                dotClass: styles.statusDotGreen,
                key: "status12",
                descKey: "status12Desc",
                hasConnector: false,
              },
            ].map(({ dotClass, key, descKey, hasConnector }) => (
              <div className={styles.statusItem} key={key}>
                <div className={styles.statusLine}>
                  <div className={`${styles.statusDot} ${dotClass}`}></div>
                  {hasConnector && <div className={styles.statusConnector}></div>}
                </div>
                <div className={styles.statusContent}>
                  <h4>{t(`shipperGuidePage.autoOrder.${key}`)}</h4>
                  <p>{t(`shipperGuidePage.autoOrder.${descKey}`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* HISTORY */}
          <h3 className={styles.sectionHeading} id="auto-order-history">
            {t("shipperGuidePage.autoOrder.historyTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoOrder.historyDesc")}</p>
          <div className={styles.cardsGrid} style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#DBEAFE" }}>
                A
              </div>
              <h4>{t("shipperGuidePage.autoOrder.geoTracking")}</h4>
              <p>{t("shipperGuidePage.autoOrder.geoTrackingDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#D1FAE5" }}>
                B
              </div>
              <h4>{t("shipperGuidePage.autoOrder.transportDocs")}</h4>
              <p>{t("shipperGuidePage.autoOrder.transportDocsDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#FEF3C7" }}>
                C
              </div>
              <h4>{t("shipperGuidePage.autoOrder.driverDocs")}</h4>
              <p>{t("shipperGuidePage.autoOrder.driverDocsDesc")}</p>
            </div>
          </div>
        </div>

        {/* AUTO: MARKETPLACE */}
        <div className={sectionClass("auto-marketplace")} id="auto-marketplace">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoMarketplace.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoMarketplace.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.autoMarketplace.callout")}</div>
          </div>

          <p className={styles.bodyText}>{t("shipperGuidePage.autoMarketplace.tabsDesc")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoMarketplace.companiesTitle")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoMarketplace.companiesDesc")}</p>

          {[
            { key: "orgData", descKey: "orgDataDesc" },
            { key: "experience", descKey: "experienceDesc" },
            { key: "geography", descKey: "geographyDesc" },
            { key: "directContact", descKey: "directContactDesc" },
            { key: "invitation", descKey: "invitationDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.autoMarketplace.${key}`)}</h4>
                <p>{t(`shipperGuidePage.autoMarketplace.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoMarketplace.candidatesTitle")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoMarketplace.candidatesDesc")}</p>

          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>{t("shipperGuidePage.autoMarketplace.tableStatus")}</th>
                <th>{t("shipperGuidePage.autoMarketplace.tableDescription")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.orange}`}>
                    {t("shipperGuidePage.autoMarketplace.statusPending")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.autoMarketplace.statusPendingDesc")}</td>
              </tr>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.green}`}>
                    {t("shipperGuidePage.autoMarketplace.statusAccepted")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.autoMarketplace.statusAcceptedDesc")}</td>
              </tr>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.blue}`}>
                    {t("shipperGuidePage.autoMarketplace.statusRejected")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.autoMarketplace.statusRejectedDesc")}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* AUTO: ANALYTICS */}
        <div className={sectionClass("auto-analytics")} id="auto-analytics">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoAnalytics.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoAnalytics.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoAnalytics.mainIndicators")}</h3>
          {[
            { key: "orderStats", descKey: "orderStatsDesc" },
            { key: "avgCost", descKey: "avgCostDesc" },
            { key: "totalMileage", descKey: "totalMileageDesc" },
            { key: "historyDynamics", descKey: "historyDynamicsDesc" },
            { key: "top10", descKey: "top10Desc" },
            { key: "onTimeByPartners", descKey: "onTimeByPartnersDesc" },
            { key: "shipmentsCount", descKey: "shipmentsCountDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.autoAnalytics.${key}`)}</h4>
                <p>{t(`shipperGuidePage.autoAnalytics.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoAnalytics.tenderAnalysis")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.autoAnalytics.tenderAnalysisDesc")}</p>
        </div>

        {/* AUTO: STAFF */}
        <div className={sectionClass("auto-staff")} id="auto-staff">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("shipperGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.autoStaff.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.autoStaff.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.autoStaff.howToAdd")}</h3>
          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`shipperGuidePage.autoStaff.step${i}`)}</h4>
                  <p>{t(`shipperGuidePage.autoStaff.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ============================================================ */}
        {/* AVIA SECTIONS */}
        {/* ============================================================ */}

        {/* AVIA: CREATE ORDER */}
        <div className={sectionClass("avia-create")} id="avia-create">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaCreate.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaCreate.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaCreate.step1Title")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.blockFrom")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.blockTo")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.blockDetails")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaCreate.step2Title")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.step2Desc")}</p>

          {/* TENDER */}
          <h3 className={styles.sectionHeading} id="avia-create-tender">
            {t("shipperGuidePage.aviaCreate.tenderTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.tenderDesc")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.aviaCreate.tenderStep1")}</h4>
                <p>{t("shipperGuidePage.aviaCreate.tenderStep1Desc")}</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>2</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.aviaCreate.tenderStep2")}</h4>
                <p>{t("shipperGuidePage.aviaCreate.tenderStep2Desc")}</p>
              </div>
            </div>
          </div>

          {/* DIRECT */}
          <h3 className={styles.sectionHeading} id="avia-create-direct">
            {t("shipperGuidePage.aviaCreate.directTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.directDesc")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.aviaCreate.directStep1")}</h4>
                <p>{t("shipperGuidePage.aviaCreate.directStep1Desc")}</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>2</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.aviaCreate.directStep2")}</h4>
                <p>{t("shipperGuidePage.aviaCreate.directStep2Desc")}</p>
              </div>
            </div>
          </div>

          {/* PUBLIC */}
          <h3 className={styles.sectionHeading} id="avia-create-public">
            {t("shipperGuidePage.aviaCreate.publicTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaCreate.publicDesc")}</p>
          <div className={styles.stepList}>
            <div className={styles.stepItem}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepContent}>
                <h4>{t("shipperGuidePage.aviaCreate.publicStep1")}</h4>
                <p>{t("shipperGuidePage.aviaCreate.publicStep1Desc")}</p>
              </div>
            </div>
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.aviaCreate.callout")}</div>
          </div>
        </div>

        {/* AVIA: PARTNERS */}
        <div className={sectionClass("avia-partners")} id="avia-partners">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaPartners.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaPartners.subtitle")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaPartners.description")}</p>
        </div>

        {/* AVIA: ORDER SECTION */}
        <div className={sectionClass("avia-order")} id="avia-order">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaOrder.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaOrder.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.aviaOrder.marketplaceCallout")}</div>
          </div>

          {/* DETAILS */}
          <h3 className={styles.sectionHeading} id="avia-order-details">
            {t("shipperGuidePage.aviaOrder.detailsTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.detailsDesc")}</p>
          <p className={styles.bodyText}>
            <strong>{t("shipperGuidePage.aviaOrder.toolsTitle")}</strong>
          </p>

          {[
            { key: "viewsAnalytics", descKey: "viewsAnalyticsDesc" },
            { key: "editing", descKey: "editingDesc" },
            { key: "copying", descKey: "copyingDesc" },
            { key: "chatPartner", descKey: "chatPartnerDesc" },
            { key: "historyFeature", descKey: "historyFeatureDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.aviaOrder.${key}`)}</h4>
                <p>{t(`shipperGuidePage.aviaOrder.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <p className={styles.bodyText} style={{ marginTop: 24 }}>
            <strong>{t("shipperGuidePage.aviaOrder.additionalMenu")}</strong>
          </p>
          <div className={styles.contextMenu}>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <ShareIcon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.aviaOrder.menuSendMarketplace")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <AlarmClockPlusIcon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.aviaOrder.menuExtendTender")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <Share2Icon size={18} color="#6D7885" />
              </span>{" "}
              {t("shipperGuidePage.aviaOrder.menuShareOrder")}
            </div>
            <div className={styles.contextMenuItem}>
              <span className={styles.cmIcon}>
                <Undo2Icon size={18} color="#E53935" />
              </span>{" "}
              {t("shipperGuidePage.aviaOrder.menuRecallOrder")}
            </div>
            <div className={`${styles.contextMenuItem} ${styles.danger}`}>
              <span className={styles.cmIcon}>
                <Trash2Icon size={18} color="#E53935" />
              </span>{" "}
              {t("shipperGuidePage.aviaOrder.menuDeleteOrder")}
            </div>
          </div>

          <div style={{ margin: "20px 0" }}>
            <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.menuSendMarketplaceDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.menuExtendTenderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.menuShareOrderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.menuRecallOrderDesc")}</p>
            <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.menuDeleteOrderDesc")}</p>
          </div>

          <p className={styles.subHeading}>{t("shipperGuidePage.aviaOrder.acceptOfferTitle")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.acceptOfferDesc")}</p>

          {/* FILTER & EXPORT */}
          <h3 className={styles.sectionHeading} id="avia-order-filter">
            {t("shipperGuidePage.aviaOrder.filterTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.filterDesc")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.exportDesc")}</p>

          {/* STATUSES */}
          <h3 className={styles.sectionHeading} id="avia-order-notif">
            {t("shipperGuidePage.aviaOrder.statusesTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.statusesDesc")}</p>

          <div className={styles.statusFlow}>
            {[
              {
                dotClass: styles.statusDotOrange,
                key: "status1",
                descKey: "status1Desc",
                hasConnector: true,
              },
              { dotClass: styles.statusDotGlobe, key: "status2", descKey: "status2Desc", hasConnector: true },
              { dotClass: styles.statusDotPink, key: "status3", descKey: "status3Desc", hasConnector: true },
              { dotClass: styles.statusDotBlue, key: "status4", descKey: "status4Desc", hasConnector: true },
              { dotClass: styles.statusDotGreen, key: "status5", descKey: "status5Desc", hasConnector: true },
              {
                dotClass: styles.statusDotOrange,
                key: "status6",
                descKey: "status6Desc",
                hasConnector: true,
              },
              {
                dotClass: styles.statusDotGreen,
                key: "status7",
                descKey: "status7Desc",
                hasConnector: false,
              },
            ].map(({ dotClass, key, descKey, hasConnector }) => (
              <div className={styles.statusItem} key={key}>
                <div className={styles.statusLine}>
                  <div className={`${styles.statusDot} ${dotClass}`}></div>
                  {hasConnector && <div className={styles.statusConnector}></div>}
                </div>
                <div className={styles.statusContent}>
                  <h4>{t(`shipperGuidePage.aviaOrder.${key}`)}</h4>
                  <p>{t(`shipperGuidePage.aviaOrder.${descKey}`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* HISTORY */}
          <h3 className={styles.sectionHeading} id="avia-order-history">
            {t("shipperGuidePage.aviaOrder.historyTitle")}
          </h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaOrder.historyDesc")}</p>
          <div className={styles.cardsGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#D1FAE5" }}>
                A
              </div>
              <h4>{t("shipperGuidePage.aviaOrder.transportDocs")}</h4>
              <p>{t("shipperGuidePage.aviaOrder.transportDocsDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#FEF3C7" }}>
                B
              </div>
              <h4>{t("shipperGuidePage.aviaOrder.driverDocs")}</h4>
              <p>{t("shipperGuidePage.aviaOrder.driverDocsDesc")}</p>
            </div>
          </div>
        </div>

        {/* AVIA: MARKETPLACE */}
        <div className={sectionClass("avia-marketplace")} id="avia-marketplace">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaMarketplace.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaMarketplace.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("shipperGuidePage.aviaMarketplace.callout")}</div>
          </div>

          <p className={styles.bodyText}>{t("shipperGuidePage.aviaMarketplace.tabsDesc")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaMarketplace.companiesTitle")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaMarketplace.companiesDesc")}</p>

          {[
            { key: "orgData", descKey: "orgDataDesc" },
            { key: "experience", descKey: "experienceDesc" },
            { key: "geography", descKey: "geographyDesc" },
            { key: "directContact", descKey: "directContactDesc" },
            { key: "invitation", descKey: "invitationDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.aviaMarketplace.${key}`)}</h4>
                <p>{t(`shipperGuidePage.aviaMarketplace.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaMarketplace.candidatesTitle")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaMarketplace.candidatesDesc")}</p>

          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>{t("shipperGuidePage.aviaMarketplace.tableStatus")}</th>
                <th>{t("shipperGuidePage.aviaMarketplace.tableDescription")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.orange}`}>
                    {t("shipperGuidePage.aviaMarketplace.statusPending")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.aviaMarketplace.statusPendingDesc")}</td>
              </tr>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.green}`}>
                    {t("shipperGuidePage.aviaMarketplace.statusAccepted")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.aviaMarketplace.statusAcceptedDesc")}</td>
              </tr>
              <tr>
                <td>
                  <span className={`${styles.tag} ${styles.blue}`}>
                    {t("shipperGuidePage.aviaMarketplace.statusRejected")}
                  </span>
                </td>
                <td>{t("shipperGuidePage.aviaMarketplace.statusRejectedDesc")}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* AVIA: ANALYTICS */}
        <div className={sectionClass("avia-analytics")} id="avia-analytics">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaAnalytics.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaAnalytics.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaAnalytics.mainIndicators")}</h3>
          {[
            { key: "orderStats", descKey: "orderStatsDesc" },
            { key: "avgCost", descKey: "avgCostDesc" },
            { key: "historyDynamics", descKey: "historyDynamicsDesc" },
            { key: "top10", descKey: "top10Desc" },
            { key: "onTimeByPartners", descKey: "onTimeByPartnersDesc" },
            { key: "shipmentsCount", descKey: "shipmentsCountDesc" },
          ].map(({ key, descKey }) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`shipperGuidePage.aviaAnalytics.${key}`)}</h4>
                <p>{t(`shipperGuidePage.aviaAnalytics.${descKey}`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("shipperGuidePage.aviaAnalytics.tenderAnalysis")}</h3>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaAnalytics.tenderAnalysisDesc")}</p>
        </div>

        {/* AVIA: STAFF */}
        <div className={sectionClass("avia-staff")} id="avia-staff">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("shipperGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("shipperGuidePage.aviaStaff.title")}</h1>
          <p className={styles.pageSubtitle}>{t("shipperGuidePage.aviaStaff.subtitle")}</p>
          <p className={styles.bodyText}>{t("shipperGuidePage.aviaStaff.description")}</p>
        </div>
      </main>

      {/* FOOTER */}
      <div className={styles.footer}>
        <p>
          {t("shipperGuidePage.footer.text")}{" "}
          <a href={`mailto:${t("shipperGuidePage.footer.email")}`}>{t("shipperGuidePage.footer.email")}</a>
        </p>
      </div>
    </div>
  );
};

export default ShipperGuidePage;
